import type { Booking } from "../lib/types";
import { evaluateWarnings } from "../lib/warnings";
import { aircraftById, lessonById, profileById } from "../lib/lookups";
import { statusLabel } from "../lib/status";
import type { BoardStore } from "../stores/board";

function boardStore(): BoardStore {
  return (window as unknown as { Alpine: { store(name: string): BoardStore } }).Alpine.store("board");
}

function ctx(b: BoardStore) {
  return {
    aircraft: b.lookups.aircraft,
    certs: b.lookups.certs,
    availability: b.lookups.availability,
    profiles: b.lookups.profiles,
  };
}

export function dispatchAlpine() {
  return {
    get rampCards(): Booking[] {
      return boardStore().bookings.filter((b) => b.status === "scheduled" || b.status === "ready");
    },
    get activeCards(): Booking[] {
      return boardStore().bookings.filter((b) =>
        b.status === "dispatched" || b.status === "airborne" || b.status === "in-progress",
      );
    },
    get closedCards(): Booking[] {
      return boardStore().bookings.filter((b) =>
        b.status === "returned" || b.status === "completed" || b.status === "closed" || b.status === "canceled",
      );
    },
    get totalCount(): number {
      return boardStore().bookings.length;
    },
    get readyCount(): number {
      return boardStore().bookings.filter((b) => b.status === "ready").length;
    },
    get airborneCount(): number {
      return boardStore().bookings.filter((b) => b.status === "airborne" || b.status === "in-progress").length;
    },
    get blockedCount(): number {
      const board = boardStore();
      return board.bookings.filter(
        (b) =>
          !b.ready &&
          b.status === "scheduled" &&
          evaluateWarnings(b, board.bookings, ctx(board)).length > 0,
      ).length;
    },
    _metricsRef: null as Booking[] | null,
    _metricsFleet: 0,
    _metricsCached: { hours: "0.0", util: "0" },
    get fleetMetrics(): { hours: string; util: string } {
      const board = boardStore();
      if (this._metricsRef === board.bookings && this._metricsFleet === board.lookups.aircraft.length) {
        return this._metricsCached;
      }
      let minutes = 0;
      for (const b of board.bookings) {
        if (!b.aircraftId) continue;
        if (b.status !== "returned" && b.status !== "completed" && b.status !== "closed") continue;
        const [sh, sm] = b.start.split(":").map(Number);
        const [eh, em] = b.end.split(":").map(Number);
        minutes += (eh * 60 + em) - (sh * 60 + sm);
      }
      const fleetCount = board.lookups.aircraft.length;
      const operatingMinutes = fleetCount * 15 * 60;
      const util = fleetCount === 0 ? "0" : Math.round((minutes / operatingMinutes) * 100).toString();
      this._metricsRef = board.bookings;
      this._metricsFleet = fleetCount;
      this._metricsCached = { hours: (minutes / 60).toFixed(1), util };
      return this._metricsCached;
    },
    get fleetHoursToday(): string {
      return this.fleetMetrics.hours;
    },
    get fleetUtilization(): string {
      return this.fleetMetrics.util;
    },
    get canAdvance(): boolean {
      const r = boardStore().role;
      return r === "Owner" || r === "Scheduler" || r === "Flight Instructor";
    },
    isReady(b: Booking): boolean {
      return b.status === "ready" || b.ready;
    },
    hasWarn(b: Booking): boolean {
      const board = boardStore();
      return evaluateWarnings(b, board.bookings, ctx(board)).length > 0;
    },
    cardClass(b: Booking): string {
      const w = this.hasWarn(b);
      const ready = this.isReady(b);
      const active = b.status === "airborne" || b.status === "dispatched" || b.status === "in-progress";
      const cls = ["dp-card"];
      if (w && !ready) cls.push("warn");
      if (ready && b.status === "ready") cls.push("ready-card");
      if (active) cls.push("airborne-card");
      return cls.join(" ");
    },
    aircraftLabel(id: string | null): string {
      if (!id) return "CLASSROOM";
      return aircraftById(boardStore().lookups.aircraft, id)?.tail ?? "—";
    },
    aircraftModel(id: string | null): string {
      if (!id) return "Class A";
      return aircraftById(boardStore().lookups.aircraft, id)?.model ?? "—";
    },
    lessonName(id: string): string {
      return lessonById(boardStore().lookups.lessons, id)?.name ?? "—";
    },
    userName(id: string | null): string {
      return profileById(boardStore().lookups.profiles, id)?.name ?? "";
    },
    statusLabel(b: Booking): string {
      return statusLabel(b.status);
    },
    warnSummary(b: Booking): string {
      const board = boardStore();
      const ws = evaluateWarnings(b, board.bookings, ctx(board));
      return `${ws.length} unresolved · ${ws.map((w) => w.kind).join(", ")}`;
    },
    nextLabel(b: Booking): string | null {
      switch (b.status) {
        case "scheduled": return "Mark ready";
        case "ready": return "Dispatch";
        case "dispatched": return "Mark airborne";
        case "airborne":
        case "in-progress": return "Mark returned";
        case "returned": return "Close";
        default: return null;
      }
    },
    async advance(b: Booking) {
      const board = boardStore();
      if (b.status === "scheduled") {
        const ws = evaluateWarnings(b, board.bookings, ctx(board));
        if (ws.length > 0 && !b.ready) {
          const msg =
            `This booking has ${ws.length} warning(s):\n\n` +
            ws.map((w) => `• ${w.title} — ${w.detail}`).join("\n") +
            "\n\nMark ready and accept these warnings?";
          if (!window.confirm(msg)) return;
        }
        await board.setStatus(b.id, "ready", true);
      } else if (b.status === "ready") {
        await board.setStatus(b.id, "dispatched");
      } else if (b.status === "dispatched") {
        await board.setStatus(b.id, "airborne");
      } else if (b.status === "airborne" || b.status === "in-progress") {
        await board.setStatus(b.id, "returned");
      } else if (b.status === "returned") {
        await board.setStatus(b.id, "closed");
      }
    },
  };
}
